"use strict";
/**
 * 行业分类表 —— AI 建站时「你属于什么行业」那个选择器的真源。
 *
 * ## 为什么分两级，而不是平铺一张长列表
 *
 * 一级回答「我是做什么的」（8 选 1），二级回答「具体卖什么」（3–6 选 1）。
 * 平铺 36 个选项让用户在长列表里扫，而他脑子里先有的是"我是五金厂"，
 * 不是"我属于工业与制造下面的五金工具"。
 *
 * 更重要的是**两级绑的是不同的东西**：
 *
 *   · 一级 → 版式 + 配色 + 文案语气（变化极慢，你后期手工维护）
 *   · 二级 → 商品示例名 + 配图提示词（会一直往上加）
 *
 * 混成一级的话，以后加一个二级品类要顺带想"它该配哪套版式"，
 * 而这件事其实早被它的一级决定了。
 *
 * ## 为什么 seeds 是中英成对，而不是两套数组
 *
 * 它有两个消费点，要的语言不一样：
 *   · 拼提示词时要英文（`Fiberglass Claw Hammer`）—— 模型据此知道这个品类长什么样
 *   · 追问时当可点的 chip 要中文（`羊角锤`）—— 用户在对话框里点两下就答完
 *
 * 分成两个平行数组，早晚会错位（加了一条中文忘了加英文），而错位是静默的：
 * 提示词里少一个词，没人看得出来。成对存，改一处就够。
 *
 * ## 为什么是常量文件，不是数据库表
 *
 * 控制台和 platform 都依赖 `@icetus/contracts`，所以一份常量就是唯一真源 ——
 * 不会重演 `BlockField` 有两份、改一处忘了另一处的老问题。加一个二级品类
 * 是加六行数据、跑一次 CI、部署一次，不需要迁移。
 *
 * 真要做到运营自己在后台加的时候，把这份数据灌进一张 `industry_taxonomy` 表、
 * 控制台改读表即可：**结构本来就是照表设计的**，接口和前端都不用动。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.TAXONOMY = exports.TAXONOMY_VERSION = void 0;
exports.industryOptions = industryOptions;
exports.findIndustry = findIndustry;
exports.findSegment = findSegment;
exports.isValidIndustry = isValidIndustry;
exports.isValidSegment = isValidSegment;
exports.pickTheme = pickTheme;
exports.matchSegmentByText = matchSegmentByText;
exports.segmentSeedsEn = segmentSeedsEn;
exports.segmentHintsZh = segmentHintsZh;
const brand_palette_1 = require("./tokens/brand-palette");
/**
 * 版本号，落进 `site_agent_runs.brief.taxonomyVersion`。
 *
 * 半年后你改了二级分类，老 run 仍然能解释"当时为什么出的是这些东西"。
 * 没有它，查历史记录时只会得出"系统当时抽风了"。
 */
exports.TAXONOMY_VERSION = '2026-11-04.1';
exports.TAXONOMY = [
    {
        id: 'industrial',
        label: '工业与制造',
        en: 'Industrial & manufacturing',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.sienna.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'hand-tools',
                label: '五金工具',
                en: 'Hand & power tools',
                aliases: ['手动工具', '手工具', '电动工具', '工具', 'hardware tools'],
                seeds: [
                    { zh: '羊角锤', en: 'Fiberglass Claw Hammer' },
                    { zh: '活动扳手', en: 'Adjustable Wrench' },
                    { zh: '无绳电钻', en: 'Cordless Drill Driver' },
                    { zh: '卷尺', en: 'Tape Measure' },
                    { zh: '工具箱', en: 'Tool Storage Case' },
                    { zh: '螺丝刀套装', en: 'Screwdriver Bit Set' },
                ],
            },
            {
                id: 'machine-parts',
                label: '机械零部件',
                en: 'Machined components',
                aliases: ['机加工', '精密零件', '五金件', 'cnc'],
                seeds: [
                    { zh: '齿轮', en: 'Steel Spur Gear' },
                    { zh: '轴承座', en: 'Bearing Housing' },
                    { zh: '法兰盘', en: 'Machined Flange' },
                    { zh: '联轴器', en: 'Flexible Coupling' },
                    { zh: '紧固螺栓', en: 'Hex Flange Bolt' },
                    { zh: '精密轴套', en: 'Precision Bushing' },
                ],
            },
            {
                id: 'industrial-equipment',
                label: '工业设备',
                en: 'Industrial equipment',
                aliases: ['机床', '包装机', '输送线', '生产线'],
                seeds: [
                    { zh: '数控车床', en: 'CNC Lathe' },
                    { zh: '自动包装机', en: 'Automatic Packaging Machine' },
                    { zh: '皮带输送机', en: 'Belt Conveyor' },
                    { zh: '工业冷水机', en: 'Industrial Chiller' },
                    { zh: '激光打标机', en: 'Laser Marking Machine' },
                ],
            },
            {
                id: 'mould-die',
                label: '模具与铸造',
                en: 'Moulds & castings',
                aliases: ['注塑模', '压铸', '铸造', '砂型'],
                seeds: [
                    { zh: '注塑模具', en: 'Injection Mould' },
                    { zh: '压铸件', en: 'Aluminium Die Casting' },
                    { zh: '砂型铸件', en: 'Sand Cast Housing' },
                    { zh: '冲压模架', en: 'Stamping Die Set' },
                    { zh: '精密铸造件', en: 'Investment Casting Part' },
                ],
            },
            {
                id: 'pump-valve',
                label: '泵阀管件',
                en: 'Pumps, valves & fittings',
                aliases: ['阀门', '水泵', '法兰', '管件'],
                seeds: [
                    { zh: '离心泵', en: 'Centrifugal Water Pump' },
                    { zh: '球阀', en: 'Stainless Ball Valve' },
                    { zh: '不锈钢法兰', en: 'Stainless Steel Flange' },
                    { zh: '止回阀', en: 'Check Valve' },
                    { zh: '工业软管接头', en: 'Industrial Hose Coupling' },
                ],
            },
            {
                id: 'welding',
                label: '焊接与耗材',
                en: 'Welding & consumables',
                aliases: ['焊机', '焊丝', '焊材', '防护'],
                seeds: [
                    { zh: '逆变焊机', en: 'Inverter Welding Machine' },
                    { zh: '药芯焊丝', en: 'Flux-Cored Welding Wire' },
                    { zh: '焊接面罩', en: 'Auto-Darkening Welding Helmet' },
                    { zh: '氩弧焊枪', en: 'TIG Welding Torch' },
                    { zh: '焊条', en: 'Stainless Welding Electrode' },
                ],
            },
        ],
    },
    {
        id: 'electronics',
        label: '电子与电气',
        en: 'Electronics & electrical',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.steel.hex,
        tone: 'tech',
        segments: [
            {
                id: 'consumer-electronics',
                label: '消费电子',
                en: 'Consumer electronics',
                aliases: ['数码', '小家电电子', '智能硬件'],
                seeds: [
                    { zh: '蓝牙音箱', en: 'Portable Bluetooth Speaker' },
                    { zh: '无线充电器', en: 'Wireless Charging Pad' },
                    { zh: 'USB 集线器', en: 'USB-C Hub' },
                    { zh: '智能手环', en: 'Fitness Tracker Band' },
                    { zh: '移动电源', en: 'Power Bank 20000mAh' },
                ],
            },
            {
                id: 'industrial-lighting',
                label: '工业照明',
                en: 'Industrial lighting',
                aliases: ['灯具', '照明', '工矿灯'],
                seeds: [
                    { zh: '工矿灯', en: 'UFO High Bay Light' },
                    { zh: '投光灯', en: 'LED Flood Light' },
                    { zh: '三防灯', en: 'Tri-Proof Linear Light' },
                    { zh: '灯管支架', en: 'LED Tube Batten' },
                    { zh: '防爆灯', en: 'Explosion-Proof Light' },
                ],
            },
            {
                id: 'cable-connector',
                label: '电缆连接器',
                en: 'Cables & connectors',
                aliases: ['线缆', '接插件', '端子'],
                seeds: [
                    { zh: '航空插头', en: 'Circular Aviation Connector' },
                    { zh: '防水接线盒', en: 'Waterproof Junction Box' },
                    { zh: '端子排', en: 'DIN Rail Terminal Block' },
                    { zh: '拖链电缆', en: 'Drag Chain Cable' },
                    { zh: '光伏连接器', en: 'Solar Panel Connector' },
                ],
            },
            {
                id: 'pcb-components',
                label: 'PCB 与元器件',
                en: 'PCB & components',
                aliases: ['线路板', '元器件', 'smt'],
                seeds: [
                    { zh: '多层电路板', en: 'Multilayer PCB' },
                    { zh: '贴片电容', en: 'SMD Ceramic Capacitor' },
                    { zh: '继电器', en: 'Power Relay' },
                    { zh: '开关电源模块', en: 'Switching Power Module' },
                    { zh: '传感器模组', en: 'Proximity Sensor Module' },
                ],
            },
            {
                id: 'power-supply',
                label: '电源与充电',
                en: 'Power supplies',
                aliases: ['适配器', '充电器', '电源'],
                seeds: [
                    { zh: '开关电源', en: 'DIN Rail Power Supply' },
                    { zh: '电源适配器', en: 'AC-DC Adapter' },
                    { zh: '车载充电器', en: 'Car Charger' },
                    { zh: '逆变器', en: 'Pure Sine Wave Inverter' },
                    { zh: '储能电池包', en: 'LiFePO4 Battery Pack' },
                ],
            },
        ],
    },
    {
        id: 'construction',
        label: '建材与工程',
        en: 'Building materials',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.stone.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'building-hardware',
                label: '建筑五金',
                en: 'Architectural hardware',
                aliases: ['门窗五金', '合页', '拉手'],
                seeds: [
                    { zh: '不锈钢合页', en: 'Stainless Door Hinge' },
                    { zh: '玻璃门拉手', en: 'Glass Door Pull Handle' },
                    { zh: '地弹簧', en: 'Floor Spring Closer' },
                    { zh: '门锁', en: 'Mortise Door Lock' },
                    { zh: '幕墙驳接件', en: 'Curtain Wall Spider Fitting' },
                ],
            },
            {
                id: 'stone-ceramic',
                label: '石材与陶瓷',
                en: 'Stone & ceramics',
                aliases: ['石材', '瓷砖', '陶瓷'],
                seeds: [
                    { zh: '花岗岩板材', en: 'Polished Granite Slab' },
                    { zh: '仿古瓷砖', en: 'Rustic Porcelain Tile' },
                    { zh: '大规格岩板', en: 'Large Format Sintered Stone' },
                    { zh: '马赛克', en: 'Glass Mosaic Tile' },
                    { zh: '石英石台面', en: 'Quartz Countertop' },
                ],
            },
            {
                id: 'door-window',
                label: '门窗幕墙',
                en: 'Doors, windows & facades',
                aliases: ['门窗', '幕墙', '铝型材'],
                seeds: [
                    { zh: '断桥铝窗', en: 'Thermal Break Aluminium Window' },
                    { zh: '铝板幕墙', en: 'Aluminium Panel Facade' },
                    { zh: '钢化玻璃门', en: 'Tempered Glass Door' },
                    { zh: '百叶窗', en: 'Aluminium Louver' },
                    { zh: '阳光房', en: 'Aluminium Sunroom' },
                ],
            },
            {
                id: 'insulation',
                label: '保温防水',
                en: 'Insulation & waterproofing',
                aliases: ['保温材料', '防水卷材', '隔热'],
                seeds: [
                    { zh: '岩棉板', en: 'Rock Wool Insulation Board' },
                    { zh: 'SBS 防水卷材', en: 'SBS Waterproof Membrane' },
                    { zh: '聚氨酯发泡料', en: 'PU Foam Insulation' },
                    { zh: 'XPS 挤塑板', en: 'XPS Foam Board' },
                    { zh: '防水涂料', en: 'Cementitious Waterproof Coating' },
                ],
            },
        ],
    },
    {
        id: 'home',
        label: '家居与家具',
        en: 'Home & furniture',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.forest.hex,
        tone: 'warm',
        segments: [
            {
                id: 'storage',
                label: '家居收纳',
                en: 'Home storage',
                aliases: ['收纳', '置物架', '整理'],
                seeds: [
                    { zh: '布艺收纳箱', en: 'Fabric Storage Bin' },
                    { zh: '衣柜分隔板', en: 'Closet Divider Shelf' },
                    { zh: '浴室置物架', en: 'Bathroom Corner Shelf' },
                    { zh: '抽屉分隔盒', en: 'Drawer Organiser Set' },
                    { zh: '鞋架', en: 'Stackable Shoe Rack' },
                ],
            },
            {
                id: 'furniture',
                label: '家具',
                en: 'Furniture',
                aliases: ['桌椅', '沙发', '柜子'],
                seeds: [
                    { zh: '实木餐桌', en: 'Solid Oak Dining Table' },
                    { zh: '布艺沙发', en: 'Fabric Three-Seat Sofa' },
                    { zh: '办公升降桌', en: 'Height-Adjustable Desk' },
                    { zh: '床头柜', en: 'Bedside Cabinet' },
                    { zh: '餐椅', en: 'Upholstered Dining Chair' },
                ],
            },
            {
                id: 'kitchenware',
                label: '厨房用品',
                en: 'Kitchenware',
                aliases: ['厨具', '锅具', '餐具'],
                seeds: [
                    { zh: '不锈钢炒锅', en: 'Stainless Wok' },
                    { zh: '不粘煎锅', en: 'Non-Stick Frying Pan' },
                    { zh: '餐具套装', en: 'Flatware Set' },
                    { zh: '厨房置物架', en: 'Kitchen Rack Organiser' },
                    { zh: '保温壶', en: 'Vacuum Insulated Flask' },
                ],
            },
            {
                id: 'bathroom',
                label: '卫浴',
                en: 'Bathroom fittings',
                aliases: ['浴室', '花洒', '龙头'],
                seeds: [
                    { zh: '恒温花洒', en: 'Thermostatic Shower Set' },
                    { zh: '浴室柜', en: 'Bathroom Vanity Cabinet' },
                    { zh: '抽拉龙头', en: 'Pull-Out Kitchen Faucet' },
                    { zh: '淋浴房', en: 'Shower Enclosure' },
                    { zh: '智能马桶盖', en: 'Smart Toilet Seat' },
                ],
            },
            {
                id: 'textile-home',
                label: '家纺布艺',
                en: 'Home textiles',
                aliases: ['床品', '窗帘', '地毯'],
                seeds: [
                    { zh: '纯棉四件套', en: 'Cotton Bedding Set' },
                    { zh: '遮光窗帘', en: 'Blackout Curtain Panel' },
                    { zh: '短毛地毯', en: 'Short Pile Area Rug' },
                    { zh: '抱枕套', en: 'Linen Cushion Cover' },
                    { zh: '毛毯', en: 'Fleece Throw Blanket' },
                ],
            },
        ],
    },
    {
        id: 'consumer',
        label: '日用消费品',
        en: 'Consumer goods',
        theme: 'aurora',
        palette: brand_palette_1.BRAND_PALETTES.clay.hex,
        tone: 'warm',
        segments: [
            {
                id: 'outdoor',
                label: '户外用品',
                en: 'Outdoor gear',
                aliases: ['露营', '户外', '运动'],
                seeds: [
                    { zh: '折叠露营椅', en: 'Folding Camping Chair' },
                    { zh: '户外保温箱', en: 'Rotomoulded Cooler Box' },
                    { zh: '天幕帐篷', en: 'Camping Tarp Shelter' },
                    { zh: '登山背包', en: 'Hiking Backpack 40L' },
                    { zh: '便携烧烤炉', en: 'Portable Charcoal Grill' },
                ],
            },
            {
                id: 'pet',
                label: '宠物用品',
                en: 'Pet supplies',
                aliases: ['宠物', '猫狗', '牵引'],
                seeds: [
                    { zh: '宠物牵引绳', en: 'Reflective Dog Leash' },
                    { zh: '猫爬架', en: 'Cat Tree Tower' },
                    { zh: '宠物窝', en: 'Pet Bed Cushion' },
                    { zh: '自动喂食器', en: 'Automatic Pet Feeder' },
                    { zh: '宠物航空箱', en: 'Pet Travel Crate' },
                ],
            },
            {
                id: 'beauty',
                label: '美妆个护',
                en: 'Beauty & personal care',
                aliases: ['美妆', '个护', '化妆工具'],
                seeds: [
                    { zh: '化妆刷套装', en: 'Makeup Brush Set' },
                    { zh: '美妆蛋', en: 'Blending Sponge' },
                    { zh: '负离子吹风机', en: 'Ionic Hair Dryer' },
                    { zh: '电动牙刷', en: 'Sonic Electric Toothbrush' },
                    { zh: '化妆收纳包', en: 'Cosmetic Travel Bag' },
                ],
            },
            {
                id: 'stationery',
                label: '文具办公',
                en: 'Stationery & office',
                aliases: ['文具', '办公', '本册'],
                seeds: [
                    { zh: '中性笔', en: 'Gel Pen Set' },
                    { zh: '笔记本', en: 'Hardcover Notebook' },
                    { zh: '文件收纳盒', en: 'Document Organiser Box' },
                    { zh: '桌面收纳架', en: 'Desktop File Sorter' },
                    { zh: '白板', en: 'Magnetic Whiteboard' },
                ],
            },
            {
                id: 'toys-gifts',
                label: '玩具礼品',
                en: 'Toys & gifts',
                aliases: ['玩具', '礼品', '工艺品'],
                seeds: [
                    { zh: '益智积木', en: 'Building Block Set' },
                    { zh: '毛绒玩偶', en: 'Plush Stuffed Toy' },
                    { zh: '定制马克杯', en: 'Custom Ceramic Mug' },
                    { zh: '拼图', en: 'Jigsaw Puzzle 1000pcs' },
                    { zh: '礼品盒', en: 'Rigid Gift Box' },
                ],
            },
        ],
    },
    {
        id: 'textile',
        label: '纺织服装',
        en: 'Textiles & apparel',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.aubergine.hex,
        tone: 'editorial',
        segments: [
            {
                id: 'fabric',
                label: '面料',
                en: 'Fabrics',
                aliases: ['布料', '针织布', '梭织'],
                seeds: [
                    { zh: '针织汗布', en: 'Single Jersey Knit Fabric' },
                    { zh: '牛仔布', en: 'Denim Fabric' },
                    { zh: '涤纶牛津布', en: 'Polyester Oxford Fabric' },
                    { zh: '法兰绒', en: 'Brushed Flannel Fabric' },
                    { zh: '功能性面料', en: 'Water-Repellent Shell Fabric' },
                ],
            },
            {
                id: 'apparel-oem',
                label: '服装代工',
                en: 'Apparel OEM',
                aliases: ['服装', '成衣', '代工'],
                seeds: [
                    { zh: '连帽卫衣', en: 'Fleece Hoodie' },
                    { zh: '工装裤', en: 'Workwear Cargo Pants' },
                    { zh: 'polo 衫', en: 'Pique Polo Shirt' },
                    { zh: '羽绒服', en: 'Down Puffer Jacket' },
                    { zh: '冲锋衣', en: 'Waterproof Shell Jacket' },
                ],
            },
            {
                id: 'bags-shoes',
                label: '箱包鞋帽',
                en: 'Bags, shoes & hats',
                aliases: ['箱包', '背包', '帽子', '鞋'],
                seeds: [
                    { zh: '双肩背包', en: 'Business Laptop Backpack' },
                    { zh: '帆布托特包', en: 'Canvas Tote Bag' },
                    { zh: '棒球帽', en: 'Embroidered Baseball Cap' },
                    { zh: '收纳腰包', en: 'Crossbody Waist Bag' },
                    { zh: '棉拖鞋', en: 'Cotton Slippers' },
                ],
            },
            {
                id: 'textile-accessories',
                label: '纺织辅料',
                en: 'Trims & accessories',
                aliases: ['辅料', '拉链', '纽扣', '织带'],
                seeds: [
                    { zh: '金属拉链', en: 'Metal Zipper' },
                    { zh: '织带', en: 'Woven Webbing Strap' },
                    { zh: '树脂纽扣', en: 'Resin Button' },
                    { zh: '刺绣标', en: 'Embroidered Patch' },
                    { zh: '松紧带', en: 'Elastic Band' },
                ],
            },
        ],
    },
    {
        id: 'auto',
        label: '汽车与配件',
        en: 'Automotive',
        theme: 'kiln',
        palette: brand_palette_1.BRAND_PALETTES.slate.hex,
        tone: 'industrial',
        segments: [
            {
                id: 'auto-parts',
                label: '汽车配件',
                en: 'Auto parts',
                aliases: ['汽配', '零部件', '改装件'],
                seeds: [
                    { zh: '刹车片', en: 'Ceramic Brake Pad Set' },
                    { zh: '空气滤清器', en: 'Engine Air Filter' },
                    { zh: '悬挂球头', en: 'Suspension Ball Joint' },
                    { zh: 'LED 车灯总成', en: 'LED Headlight Assembly' },
                    { zh: '轮毂', en: 'Alloy Wheel Rim' },
                ],
            },
            {
                id: 'motorcycle',
                label: '摩托与电动车',
                en: 'Motorcycle & e-bike',
                aliases: ['摩托', '电动车', '自行车'],
                seeds: [
                    { zh: '摩托车后视镜', en: 'Motorcycle Mirror Pair' },
                    { zh: '电动车控制器', en: 'E-Bike Motor Controller' },
                    { zh: '锂电池组', en: 'E-Bike Battery Pack' },
                    { zh: '链条', en: 'Motorcycle Drive Chain' },
                    { zh: '车把护手', en: 'Handlebar Hand Guard' },
                ],
            },
            {
                id: 'car-accessories',
                label: '车载用品',
                en: 'Car accessories',
                aliases: ['车载', '车用', '内饰'],
                seeds: [
                    { zh: '车载手机支架', en: 'Magnetic Phone Mount' },
                    { zh: '汽车脚垫', en: 'TPE Floor Mat Set' },
                    { zh: '行车记录仪', en: 'Dash Camera' },
                    { zh: '座椅套', en: 'Seat Cover Set' },
                    { zh: '车载吸尘器', en: 'Cordless Car Vacuum' },
                ],
            },
        ],
    },
    {
        id: 'medical',
        label: '医疗与化工',
        en: 'Medical & chemical',
        theme: 'meridian',
        palette: brand_palette_1.BRAND_PALETTES.teal.hex,
        tone: 'minimal',
        segments: [
            {
                id: 'medical-device',
                label: '医疗器械',
                en: 'Medical devices',
                aliases: ['器械', '耗材', '医械'],
                seeds: [
                    { zh: '血压计', en: 'Digital Blood Pressure Monitor' },
                    { zh: '雾化器', en: 'Compressor Nebuliser' },
                    { zh: '轮椅', en: 'Folding Wheelchair' },
                    { zh: '手术器械包', en: 'Surgical Instrument Kit' },
                    { zh: '医用病床', en: 'Electric Hospital Bed' },
                ],
            },
            {
                id: 'lab-supplies',
                label: '实验室耗材',
                en: 'Lab supplies',
                aliases: ['实验室', '耗材', '试剂'],
                seeds: [
                    { zh: '离心管', en: 'Centrifuge Tube 50mL' },
                    { zh: '移液枪头', en: 'Pipette Tips' },
                    { zh: '培养皿', en: 'Petri Dish' },
                    { zh: '玻璃量筒', en: 'Glass Measuring Cylinder' },
                    { zh: '防护手套', en: 'Nitrile Examination Gloves' },
                ],
            },
            {
                id: 'fine-chemical',
                label: '精细化工',
                en: 'Fine chemicals',
                aliases: ['化工', '助剂', '原料'],
                seeds: [
                    { zh: '工业清洗剂', en: 'Industrial Degreaser' },
                    { zh: '水性涂料', en: 'Water-Based Coating' },
                    { zh: '硅油', en: 'Silicone Fluid' },
                    { zh: '表面活性剂', en: 'Nonionic Surfactant' },
                    { zh: '环氧树脂', en: 'Epoxy Resin' },
                ],
            },
            {
                id: 'packaging-material',
                label: '包装材料',
                en: 'Packaging materials',
                aliases: ['包装', '纸箱', '塑料包装'],
                seeds: [
                    { zh: '瓦楞纸箱', en: 'Corrugated Shipping Box' },
                    { zh: 'PE 包装袋', en: 'PE Packaging Bag' },
                    { zh: '吸塑托盘', en: 'Blister Tray' },
                    { zh: '缠绕膜', en: 'Stretch Wrap Film' },
                    { zh: '标签贴纸', en: 'Printed Label Sticker' },
                ],
            },
        ],
    },
];
/** 全部一级行业的选项（控制台直接渲染） */
function industryOptions() {
    return exports.TAXONOMY.map((item) => ({ id: item.id, label: item.label }));
}
function findIndustry(id) {
    if (!id)
        return null;
    return exports.TAXONOMY.find((item) => item.id === id) ?? null;
}
function findSegment(industryId, segmentId) {
    if (!segmentId)
        return null;
    const industry = findIndustry(industryId);
    return industry?.segments.find((item) => item.id === segmentId) ?? null;
}
function isValidIndustry(id) {
    return exports.TAXONOMY.some((item) => item.id === id);
}
/** `segment` 必须属于这个 `industry` —— 只验它自身存在是不够的 */
function isValidSegment(industryId, segmentId) {
    return Boolean(findSegment(industryId, segmentId));
}
/**
 * 行业 → 版式。
 *
 * 认不出的一律退回 `aurora`：**不要抛错**。这个函数的调用点在建站事务里，
 * 抛出去等于"客户填了个我们没见过的行业词，站建不出来"，
 * 而它其实只是一套配色没定而已。
 */
function pickTheme(industryId) {
    return findIndustry(industryId)?.theme ?? 'aurora';
}
/**
 * 按客户手写的一句话归类到已有的二级品类。
 *
 * 只做**别名精确/包含**匹配，不调模型 —— 模型归类放在 provider 里，
 * 那是另一条路（`segmentFallback`）。这里负责把"手动工具""手工具"这类
 * 明显是同一件事的词直接命中，省一次调用、也不会归错。
 */
function matchSegmentByText(industryId, text) {
    const industry = findIndustry(industryId);
    if (!industry)
        return null;
    const needle = text.trim().toLowerCase();
    if (!needle)
        return null;
    for (const segment of industry.segments) {
        const words = [segment.label, segment.en, ...(segment.aliases ?? [])];
        for (const word of words) {
            const hay = word.toLowerCase();
            // 双向包含：客户写"手动工具类"，别名是"手动工具"（别名被包含）；
            // 客户写"工具"，label 是"五金工具"（label 被包含）
            if (needle === hay || needle.includes(hay) || hay.includes(needle)) {
                return segment;
            }
        }
    }
    return null;
}
/** 一个二级品类的英文名提示，拼进提示词。没有种子时给空串 */
function segmentSeedsEn(segment) {
    return segment ? segment.seeds.map((item) => item.en).join(', ') : '';
}
/** 追问时当 chip 用的中文建议，取前 `count` 条 */
function segmentHintsZh(segment, count = 5) {
    return segment ? segment.seeds.slice(0, count).map((item) => item.zh) : [];
}
//# sourceMappingURL=taxonomy.js.map